// Silent updates on the Workbox worker (src/sw.ts). The app acts for the person and never asks:
// a new worker installs and downloads in the background and then WAITS, so nothing swaps under a
// running tasting. It is applied (skip waiting, then one controlled reload) at the moments a reload
// costs nothing: a cold start, and the person switching main-menu option (ui/TabBar). Never
// mid-screen, and never while a sign-in is round-tripping through its provider (holdUpdates).
// A reload keeps the route (the hash) and every store, and touches no session: the cookie is the
// edge's, the remembered account is localStorage (lib/social/auth.ts).
import { registerSW } from "virtual:pwa-register";

/** How often a running app asks for a new worker, on top of start, focus and becoming visible. */
export const CHECK_INTERVAL_MS = 60 * 60 * 1000;

/** Set just before an update reloads the page, so the next cold start does not apply another. */
const APPLIED_KEY = "proef:update-applied";

export interface UpdateEnv {
  readonly reload: () => void;
  readonly storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | undefined;
}

const defaultEnv = (): UpdateEnv => {
  let storage: Storage | undefined;
  try {
    ({ sessionStorage: storage } = window);
  } catch {
    storage = undefined;
  }
  return { reload: () => window.location.reload(), storage };
};

let env: UpdateEnv = { reload: () => undefined, storage: undefined };
let updateSW: ((reload?: boolean) => Promise<void>) | undefined;
/** A new worker is installed and waiting for the right moment. */
let waiting = false;
/** This page asked the worker to take over, so its takeover is the reload we want. */
let applying = false;
/** Another window already switched the worker: this page runs an older bundle until it reloads. */
let takenOver = false;
let holds = 0;
let started = false;

/** Choices the retired update switches (check by itself, notify) left on devices; nothing reads them now. */
function forgetRetiredChoices(): void {
  try {
    window.localStorage.removeItem("proef.updates");
  } catch {
    // Storage blocked: nothing was stored either.
  }
}

/**
 * Keeps updates from being applied until the returned release is called, for work that a reload
 * would cut in half (a sign-in leaving for its provider and coming back). A held update is not
 * dropped: it applies at the next main-menu switch.
 */
export function holdUpdates(): () => void {
  holds += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds -= 1;
  };
}

function remember(): void {
  try {
    env.storage?.setItem(APPLIED_KEY, "1");
  } catch {
    // Storage blocked: a cold start may apply once more, which skipWaiting makes a no-op.
  }
}

function apply(): void {
  if (applying || holds > 0) return;
  if (takenOver) {
    remember();
    env.reload();
    return;
  }
  if (!waiting || updateSW === undefined) return;
  applying = true;
  waiting = false;
  remember();
  // Messages the worker to skip waiting; onNeedReload below reloads once it controls this page.
  updateSW(true).catch(() => {
    applying = false;
  });
}

/**
 * Called by the tab bar when the person moves to another main-menu option, after the route has
 * changed, so the reload lands on the screen they chose. Does nothing when no update waits.
 */
export function updateOnMainSwitch(): void {
  apply();
}

/** A cold start applies a waiting worker once; an OAuth return in the address bar is left alone. */
function applyAtColdStart(): void {
  let justApplied = false;
  try {
    justApplied = env.storage?.getItem(APPLIED_KEY) === "1";
    env.storage?.removeItem(APPLIED_KEY);
  } catch {
    justApplied = false;
  }
  if (justApplied || new URLSearchParams(window.location.search).has("auth")) return;
  apply();
}

/** Asks the browser to re-fetch sw.js; offline or an edge error simply means "not now". */
async function checkForUpdate(registration: ServiceWorkerRegistration): Promise<void> {
  try {
    await registration.update();
  } catch {
    // The next focus, visibility change or hour asks again.
  }
}

/**
 * Registers the worker and keeps asking for a newer one: now, whenever the app comes back to the
 * front and every hour. Called once from main.tsx; a second call does nothing. `own` is a test
 * seam for the reload and the storage.
 */
export function startUpdates(own: UpdateEnv = defaultEnv()): void {
  if (started || !("serviceWorker" in navigator)) return;
  started = true;
  env = own;
  forgetRetiredChoices();
  let registration: ServiceWorkerRegistration | undefined;
  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      waiting = true;
    },
    // The plugin reloads on any takeover, including one another window asked for, which would
    // reload this page mid-tasting; only a takeover this page asked for reloads now.
    onNeedReload() {
      if (applying) {
        env.reload();
        return;
      }
      takenOver = true;
    },
    onRegisteredSW(_url, registered) {
      registration = registered;
      if (registered?.waiting) {
        waiting = true;
        applyAtColdStart();
      }
    },
    onRegisterError(error: unknown) {
      console.warn("proef: service worker registration failed", error);
    },
  });
  const check = (): void => {
    if (registration !== undefined) void checkForUpdate(registration);
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  window.addEventListener("focus", check);
  window.setInterval(check, CHECK_INTERVAL_MS);
  // The first check waits for the registration started above.
  window.addEventListener("load", check, { once: true });
}
